import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Calendar, Wallet } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import BottomTabBar from '../components/BottomTabBar';
import PaymentCard from '../components/PaymentCard';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import { getPayments } from '../api/paymentsApi';
import { useToast } from '../context/ToastContext';
import PullToRefresh from '../components/PullToRefresh';

function getDefaultWeekRange() {
  const today = new Date();
  const day = today.getDay();
  const diffToMonday = day === 0 ? 6 : day - 1;
  const monday = new Date(today);
  monday.setDate(today.getDate() - diffToMonday);

  const toDateStr = (d) => d.toISOString().split('T')[0];
  return { from: toDateStr(monday), to: toDateStr(today) };
}

function Payments() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const defaultRange = getDefaultWeekRange();

  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [toDate, setToDate] = useState(defaultRange.to);
  const [showFilter, setShowFilter] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchPayments = async (pageNum = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const result = await getPayments(fromDate, toDate, pageNum, 10);
      if (result.success) {
        setPayments((prev) => (append ? [...prev, ...result.payments] : result.payments));
        setHasMore(result.has_more);
        setPage(pageNum);
      } else {
        setError(result.message || t('common.genericError'));
        showToast('error', result.message || t('common.genericError'));
      }
    } catch (err) {
      setError(t('common.networkError'));
      showToast('error', t('common.networkError'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchPayments(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const handleLoadMore = () => {
    fetchPayments(page + 1, true);
  };

  const handleRetry = () => {
    fetchPayments(1);
  };

  const totalPending = payments
    .filter((p) => p.pay_status !== 'paid')
    .reduce((sum, p) => sum + Number(p.amount_value), 0);

  const isDefaultWeek = fromDate === defaultRange.from && toDate === defaultRange.to;

  const handleReset = () => {
    setFromDate(defaultRange.from);
    setToDate(defaultRange.to);
  };

  return (
   <div style={{ minHeight: '100vh', paddingBottom: 'calc(64px + env(safe-area-inset-bottom))', backgroundColor: '#f9fafb' }}>
      <AppHeader
        title={t('payments.title')}
        showLogo={true}
        rightAction={
          <button onClick={() => setShowFilter(!showFilter)} style={styles.filterIconBtn}>
            <Calendar size={20} color="#2563eb" />
          </button>
        }
      />

 <PullToRefresh onRefresh={() => fetchPayments(1)}>
      <div style={{ padding: 16 }}>
        {showFilter && (
          <div style={styles.filterPanel}>
            <div style={styles.filterRow}>
              <div style={{ flex: 1 }}>
                <label style={styles.filterLabel}>{t('payments.filterFrom')}</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={styles.dateInput}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.filterLabel}>{t('payments.filterTo')}</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={styles.dateInput}
                />
              </div>
            </div>
            {!isDefaultWeek && (
              <button onClick={handleReset} style={styles.resetLink}>
                {t('payments.resetFilter')}
              </button>
            )}
          </div>
        )}

        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>
            {isDefaultWeek ? t('payments.totalThisWeek') : t('payments.totalForPeriod')}
          </div>
          <div style={styles.summaryAmount}>${totalPending.toLocaleString()}</div>
        </div>

        {loading && <LoadingSpinner fullScreen />}

        {!loading && error && (
          <EmptyState
            icon={Wallet}
            title={t('common.genericError')}
            message={error}
            onRetry={handleRetry}
            retryLabel={t('common.tryAgain')}
          />
        )}

        {!loading && !error && payments.length === 0 && (
          <EmptyState
            icon={Wallet}
            title={t('payments.emptyTitle')}
            message={t('payments.emptyMessage')}
          />
        )}

        {!loading && !error && payments.map((payment) => (
          <PaymentCard key={payment.id_payment} payment={payment} />
        ))}

       {!loading && !error && hasMore && (
  <button onClick={handleLoadMore} disabled={loadingMore} style={styles.loadMoreBtn}>
    {loadingMore ? t('common.loading') : t('common.loadMore')}
  </button>
)}
      </div>
	  </PullToRefresh>

      <BottomTabBar />
    </div>
  );
}

const styles = {
  filterIconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 6 },
  filterPanel: { backgroundColor: 'white', borderRadius: 12, padding: 14, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  filterRow: { display: 'flex', gap: 10 },
  filterLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 },
  dateInput: { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box' },
  resetLink: { marginTop: 10, background: 'none', border: 'none', color: '#2563eb', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 },
  summaryCard: { backgroundColor: '#2563eb', borderRadius: 14, padding: 20, marginBottom: 20 },
  summaryLabel: { color: '#dbeafe', fontSize: 13, fontWeight: 600, marginBottom: 6 },
  summaryAmount: { color: 'white', fontSize: 30, fontWeight: 800 },
  loadMoreBtn: {
    width: '100%',
    padding: '12px 0',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    color: '#2563eb',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    marginTop: 8,
  },
};

export default Payments;